"use client";

import Link from "next/link";
import type { components } from "@theta/api-client";
import { formatShortDateTime } from "@/lib/dates";
import { cn } from "./ui/cn";
import { TimeAgo } from "./TimeAgo";

type SearchHit = components["schemas"]["SearchResult"];

/**
 * ONE SEARCH RESULT, DRAWN ONCE
 *
 * The header dropdown and `/soeg` each had their own copy of this row — the
 * same three icons, the same title line, and two meta lines that had already
 * drifted apart: the dropdown wrote "i {gruppe}" under a post and the page
 * wrote "{gruppe}". `useSiteSearch`'s header says the deleted
 * `lib/api/search.ts` existed because those two surfaces "had each written
 * that flattening out by hand and could drift apart"; the flattening moved to
 * the server, and the RENDERING quietly took its place. So it lives here.
 *
 * WHAT A ROW MAY SHOW IS DECIDED BY THE CONTRACT, NOT BY THIS FILE. `GET
 * /search` states it plainly (schemas/search.ts, "A HIT IS A POINTER, NOT A
 * REPRESENTATION"): a hit carries an id, a human title, an address and just
 * enough context to draw one row — deliberately not the post's body, its
 * author, its counts or its attachments, because nothing ACTS on a search hit
 * and the reader follows `permalink` for the thing itself.
 *
 * So this renders everything a hit DOES carry and invents nothing:
 *
 *   post   — the label (the author's title, else the first 80 characters of
 *            the body, decided server-side by `postLabel` so one post reads
 *            the same wherever it is found), its group, and when it was
 *            written.
 *   group  — the name, and whether it is private.
 *   event  — the name, WHEN IT STARTS, and whether it is private. An event
 *            result that does not say when it is was the emptiest of the
 *            three (OSO-360).
 *
 * `createdAt` is on every arm; it is shown only for posts. On an event it
 * would sit beside `startsAt` saying something duller about the same row, and
 * on a group "oprettet for 2 år siden" is not why anybody searched.
 */

/** A hit whose subject is not public — drawn beside the kind, never alone. */
function LockIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="inline-block shrink-0 -mt-px"
      aria-label="Privat"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function ResultIcon({ type }: { type: SearchHit["resultType"] }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-subtle-foreground shrink-0"
    >
      {type === "group" ? (
        <>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </>
      ) : type === "event" ? (
        <>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </>
      ) : (
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      )}
    </svg>
  );
}

export function SearchResultRow({
  result,
  onNavigate,
  className,
}: {
  result: SearchHit;
  /** Called on click — the dropdown uses it to close and clear itself. */
  onNavigate?: () => void;
  className?: string;
}) {
  return (
    <Link
      // The server's own permalink for the hit — neither surface builds a URL
      // out of parts any more.
      href={result.permalink}
      onClick={onNavigate}
      className={cn(
        "flex items-center gap-3 px-4 py-2.5 hover:bg-surface-2 transition-colors",
        className,
      )}
    >
      <ResultIcon type={result.resultType} />

      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground truncate">
          {result.title}
        </p>
        <p className="flex items-center gap-1.5 text-xs text-subtle-foreground mt-0.5">
          {result.resultType === "post" && (
            <>
              <span className="truncate">i {result.group.name}</span>
              <span aria-hidden>·</span>
              <TimeAgo date={result.createdAt} />
            </>
          )}

          {result.resultType === "group" && (
            <>
              <span>Gruppe</span>
              {result.privacyLevel !== "public_group" && <LockIcon />}
            </>
          )}

          {result.resultType === "event" && (
            <>
              <span>Begivenhed</span>
              <span aria-hidden>·</span>
              <span className="truncate">
                {formatShortDateTime(result.startsAt)}
              </span>
              {result.isPrivate && <LockIcon />}
            </>
          )}
        </p>
      </div>
    </Link>
  );
}
